<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const isSavingSettings = ref(false);
const isLoadingProducts = ref(false);
const isSubmittingCatalog = ref(false);
const isSubmittingProduct = ref(false);
const editingCatalogId = ref('');
const editingProductId = ref('');
const deletingCatalogId = ref('');
const deletingProductId = ref('');
const hasLoaded = ref(false);
const settings = ref({
  is_catalog_visible: false,
  is_cart_enabled: false,
});
const catalogs = ref([]);
const products = ref([]);
const selectedCatalogId = ref('');
const productSearch = ref('');
const catalogForm = ref({
  name: '',
  vertical: 'commerce',
});
const productForm = ref({
  name: '',
  retailer_id: '',
  description: '',
  price: '',
  currency: 'USD',
  availability: 'in stock',
  url: '',
  image_url: '',
});

const hasCatalogs = computed(() => catalogs.value.length > 0);
const requiresBusinessReauth = computed(
  () => !props.inbox?.provider_config?.meta_business_id
);
const selectedCatalog = computed(() =>
  catalogs.value.find(catalog => catalog.id === selectedCatalogId.value) || null
);
const availabilityOptions = computed(() => [
  { value: 'in stock', label: 'In stock' },
  { value: 'out of stock', label: 'Out of stock' },
  { value: 'available for order', label: 'Available for order' },
]);
const isEditingCatalog = computed(() => !!editingCatalogId.value);
const isEditingProduct = computed(() => !!editingProductId.value);
const canSubmitCatalog = computed(() => !!catalogForm.value.name.trim());
const canSubmitProduct = computed(() => {
  return (
    selectedCatalogId.value &&
    productForm.value.name.trim() &&
    productForm.value.retailer_id.trim() &&
    productForm.value.price.trim() &&
    productForm.value.currency.trim()
  );
});
const filteredProducts = computed(() => {
  const query = productSearch.value.trim().toLowerCase();
  if (!query) return products.value;

  return products.value.filter(product =>
    [product.name, product.retailer_id, product.description, product.currency]
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(query))
  );
});
const commerceStats = computed(() => ({
  catalogs: catalogs.value.length,
  products: products.value.length,
  visible: settings.value.is_catalog_visible,
  cart: settings.value.is_cart_enabled,
}));

const applyCatalogs = nextCatalogs => {
  catalogs.value = nextCatalogs || [];
  const hasSelectedCatalog = catalogs.value.some(
    catalog => catalog.id === selectedCatalogId.value
  );
  selectedCatalogId.value = hasSelectedCatalog
    ? selectedCatalogId.value
    : catalogs.value[0]?.id || '';
};

const resetCatalogForm = () => {
  catalogForm.value = {
    name: '',
    vertical: 'commerce',
  };
};

const hydrateCatalogForm = catalog => {
  catalogForm.value = {
    name: catalog?.name || '',
    vertical: catalog?.vertical || 'commerce',
  };
};

const resetProductForm = () => {
  productForm.value = {
    name: '',
    retailer_id: '',
    description: '',
    price: '',
    currency: 'USD',
    availability: 'in stock',
    url: '',
    image_url: '',
  };
};

const reloadProductsForSelectedCatalog = async () => {
  if (selectedCatalogId.value) {
    await loadProducts();
    return;
  }

  products.value = [];
  cancelEditingProduct();
};

const hydrateProductForm = product => {
  productForm.value = {
    name: product?.name || '',
    retailer_id: product?.retailer_id || '',
    description: product?.description || '',
    price: product?.price || '',
    currency: product?.currency || 'USD',
    availability: product?.availability || 'in stock',
    url: product?.url || '',
    image_url: product?.image_url || '',
  };
};

const loadProducts = async () => {
  if (!selectedCatalogId.value) {
    products.value = [];
    editingProductId.value = '';
    return;
  }

  isLoadingProducts.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppCatalogProducts(
      props.inbox.id,
      selectedCatalogId.value
    );
    products.value = response.data?.payload || [];
  } catch (error) {
    products.value = [];
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCTS_ERROR'));
  } finally {
    isLoadingProducts.value = false;
  }
};

const loadCommerce = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppCommerce(props.inbox.id);
    settings.value = {
      is_catalog_visible: !!response.data?.settings?.is_catalog_visible,
      is_cart_enabled: !!response.data?.settings?.is_cart_enabled,
    };
    applyCatalogs(response.data?.catalogs || []);
    await reloadProductsForSelectedCatalog();
    hasLoaded.value = true;
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSavingSettings.value = true;
  try {
    const response = await InboxesAPI.updateWhatsAppCommerce(
      props.inbox.id,
      settings.value
    );
    settings.value = {
      is_catalog_visible: !!response.data?.settings?.is_catalog_visible,
      is_cart_enabled: !!response.data?.settings?.is_cart_enabled,
    };
    applyCatalogs(response.data?.catalogs || catalogs.value);
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_SETTINGS_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_SETTINGS_ERROR')
    );
  } finally {
    isSavingSettings.value = false;
  }
};

const createCatalog = async () => {
  isSubmittingCatalog.value = true;
  try {
    const response = await InboxesAPI.createWhatsAppCatalog(
      props.inbox.id,
      catalogForm.value
    );
    applyCatalogs(response.data?.catalogs || catalogs.value);
    selectedCatalogId.value =
      response.data?.catalog?.id || catalogs.value[catalogs.value.length - 1]?.id || selectedCatalogId.value;
    resetCatalogForm();
    await reloadProductsForSelectedCatalog();
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_ERROR')
    );
  } finally {
    isSubmittingCatalog.value = false;
  }
};

const updateCatalog = async () => {
  isSubmittingCatalog.value = true;
  try {
    const response = await InboxesAPI.updateWhatsAppCatalog(
      props.inbox.id,
      editingCatalogId.value,
      catalogForm.value
    );
    applyCatalogs(response.data?.catalogs || catalogs.value);
    cancelEditingCatalog();
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_UPDATE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_UPDATE_ERROR')
    );
  } finally {
    isSubmittingCatalog.value = false;
  }
};

const submitCatalog = async () => {
  if (isEditingCatalog.value) {
    await updateCatalog();
    return;
  }

  await createCatalog();
};

const startEditingCatalog = catalog => {
  editingCatalogId.value = catalog.id;
  hydrateCatalogForm(catalog);
};

const cancelEditingCatalog = () => {
  editingCatalogId.value = '';
  resetCatalogForm();
};

const deleteCatalog = async catalog => {
  deletingCatalogId.value = catalog.id;
  try {
    const response = await InboxesAPI.deleteWhatsAppCatalog(
      props.inbox.id,
      catalog.id
    );
    applyCatalogs(response.data?.catalogs || []);
    if (editingCatalogId.value === catalog.id) {
      cancelEditingCatalog();
    }
    await reloadProductsForSelectedCatalog();
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_DELETE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_DELETE_ERROR')
    );
  } finally {
    deletingCatalogId.value = '';
  }
};

const createProduct = async () => {
  isSubmittingProduct.value = true;
  try {
    const response = await InboxesAPI.createWhatsAppCatalogProduct(
      props.inbox.id,
      selectedCatalogId.value,
      productForm.value
    );
    products.value = response.data?.payload || products.value;
    resetProductForm();
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_ERROR')
    );
  } finally {
    isSubmittingProduct.value = false;
  }
};

const updateProduct = async () => {
  isSubmittingProduct.value = true;
  try {
    const response = await InboxesAPI.updateWhatsAppCatalogProduct(
      props.inbox.id,
      selectedCatalogId.value,
      editingProductId.value,
      productForm.value
    );
    products.value = response.data?.payload || products.value;
    editingProductId.value = '';
    resetProductForm();
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_UPDATE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_UPDATE_ERROR')
    );
  } finally {
    isSubmittingProduct.value = false;
  }
};

const submitProduct = async () => {
  if (isEditingProduct.value) {
    await updateProduct();
    return;
  }

  await createProduct();
};

const startEditingProduct = product => {
  editingProductId.value = product.id;
  hydrateProductForm(product);
};

const cancelEditingProduct = () => {
  editingProductId.value = '';
  resetProductForm();
};

const deleteProduct = async product => {
  deletingProductId.value = product.id;
  try {
    const response = await InboxesAPI.deleteWhatsAppCatalogProduct(
      props.inbox.id,
      selectedCatalogId.value,
      product.id
    );
    products.value = response.data?.payload || [];
    if (editingProductId.value === product.id) {
      cancelEditingProduct();
    }
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_DELETE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_DELETE_ERROR')
    );
  } finally {
    deletingProductId.value = '';
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadCommerce();
  },
  { immediate: true }
);

watch(selectedCatalogId, () => {
  if (hasLoaded.value) {
    cancelEditingProduct();
    loadProducts();
  }
});
</script>

<template>
  <div class="grid gap-4">
    <div
      v-if="hasLoaded"
      class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_STAT_CATALOGS') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ commerceStats.catalogs }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_STAT_PRODUCTS') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ commerceStats.products }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_VISIBLE_LABEL') }}
        </p>
        <p class="mb-0 text-lg font-semibold text-n-slate-12">
          {{
            commerceStats.visible
              ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_STATUS_ACTIVE')
              : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_STATUS_INACTIVE')
          }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CART_LABEL') }}
        </p>
        <p class="mb-0 text-lg font-semibold text-n-slate-12">
          {{
            commerceStats.cart
              ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_STATUS_ACTIVE')
              : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_STATUS_INACTIVE')
          }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_NOTE') }}
      </p>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_REFRESH')"
        :is-loading="isLoading"
        @click="loadCommerce"
      />
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_LOADING') }}
    </div>

    <template v-else>
      <div
        v-if="requiresBusinessReauth"
        class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900"
      >
        <p class="mb-1 font-medium">
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_REAUTHORIZE_TITLE'
            )
          }}
        </p>
        <p class="mb-0">
          {{
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_REAUTHORIZE_DESCRIPTION'
            )
          }}
        </p>
      </div>

      <div class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <div class="grid gap-3 md:grid-cols-2">
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="settings.is_catalog_visible" type="checkbox" />
            <span>
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_VISIBLE_LABEL'
                )
              }}
            </span>
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="settings.is_cart_enabled" type="checkbox" />
            <span>
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CART_LABEL')
              }}
            </span>
          </label>
        </div>
        <div class="flex justify-end">
          <Button
            type="button"
            color="blue"
            icon="i-ph-floppy-disk"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_SAVE')"
            :is-loading="isSavingSettings"
            @click="saveSettings"
          />
        </div>
      </div>

      <div
        class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_SECTION') }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_META_NOTE')
          }}
        </p>

        <div class="grid gap-4 rounded-xl border border-n-container bg-n-background p-4">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_FORM_TITLE')
            }}
          </p>
          <woot-input
            v-model="catalogForm.name"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_NAME')"
          />
          <div class="flex flex-wrap justify-end gap-2">
            <Button
              v-if="isEditingCatalog"
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-x"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_CANCEL')
              "
              @click="cancelEditingCatalog"
            />
            <Button
              type="button"
              color="blue"
              :icon="isEditingCatalog ? 'i-ph-floppy-disk' : 'i-ph-plus'"
              :label="
                $t(
                  isEditingCatalog
                    ? 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_UPDATE'
                    : 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_CREATE'
                )
              "
              :disabled="!canSubmitCatalog"
              :is-loading="isSubmittingCatalog"
              @click="submitCatalog"
            />
          </div>
        </div>

        <div
          v-if="!hasCatalogs"
          class="rounded-xl border border-dashed border-n-container px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_EMPTY') }}
        </div>

        <template v-else>
          <div class="grid gap-3">
            <div
              v-for="catalog in catalogs"
              :key="catalog.id"
              class="rounded-xl border p-3"
              :class="
                selectedCatalogId === catalog.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-container bg-n-background'
              "
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ catalog.name || catalog.id }}
                  </p>
                  <p class="mt-1 mb-0 text-xs text-n-slate-11">
                    {{ catalog.id }}
                    <span v-if="catalog.vertical">
                      · {{ catalog.vertical }}
                    </span>
                  </p>
                </div>
                <div class="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="faded"
                    color="slate"
                    icon="i-ph-check-circle"
                    :label="
                      $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_SELECT')
                    "
                    @click="selectedCatalogId = catalog.id"
                  />
                  <Button
                    type="button"
                    variant="faded"
                    color="slate"
                    icon="i-ph-pencil-simple"
                    :label="
                      $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_EDIT')
                    "
                    @click="startEditingCatalog(catalog)"
                  />
                  <Button
                    type="button"
                    variant="faded"
                    color="ruby"
                    icon="i-ph-trash"
                    :label="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG_DELETE'
                      )
                    "
                    :is-loading="deletingCatalogId === catalog.id"
                    @click="deleteCatalog(catalog)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="grid gap-4 lg:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_CATALOG') }}
              </span>
              <select
                v-model="selectedCatalogId"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option
                  v-for="catalog in catalogs"
                  :key="catalog.id"
                  :value="catalog.id"
                >
                  {{ catalog.name || catalog.id }}
                </option>
              </select>
            </label>
          </div>

          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCTS_SECTION') }}
          </p>

          <p
            v-if="selectedCatalog"
            class="mb-0 text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCTS_ACTIVE_CATALOG', {
                catalog: selectedCatalog.name || selectedCatalog.id,
              })
            }}
          </p>

          <woot-input
            v-model="productSearch"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_SEARCH')"
            :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_SEARCH_PLACEHOLDER')"
          />

          <div class="grid gap-4 lg:grid-cols-2">
            <woot-input
              v-model="productForm.name"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_NAME')"
            />
            <woot-input
              v-model="productForm.retailer_id"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_RETAILER')
              "
            />
            <woot-input
              v-model="productForm.price"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_PRICE')"
            />
            <woot-input
              v-model="productForm.currency"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_CURRENCY')
              "
            />
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_AVAILABILITY') }}
              </span>
              <select
                v-model="productForm.availability"
                class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              >
                <option
                  v-for="option in availabilityOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <woot-input
              v-model="productForm.url"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_URL')"
            />
            <woot-input
              v-model="productForm.image_url"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_IMAGE')
              "
            />
          </div>

          <TextArea
            v-model="productForm.description"
            :label="
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_DESCRIPTION')
            "
            auto-height
            min-height="4rem"
          />

          <div
            v-if="productForm.image_url"
            class="flex items-center gap-3 rounded-xl border border-n-container bg-n-background p-3"
          >
            <img
              :src="productForm.image_url"
              alt=""
              class="h-16 w-16 rounded-lg border border-n-container object-cover"
            />
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_IMAGE_PREVIEW') }}
            </p>
          </div>

          <div class="flex flex-wrap justify-end gap-2">
            <Button
              v-if="isEditingProduct"
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-x"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_CANCEL')
              "
              @click="cancelEditingProduct"
            />
            <Button
              type="button"
              color="blue"
              :icon="isEditingProduct ? 'i-ph-floppy-disk' : 'i-ph-plus'"
              :label="
                $t(
                  isEditingProduct
                    ? 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_UPDATE'
                    : 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_CREATE'
                )
              "
              :disabled="!canSubmitProduct"
              :is-loading="isSubmittingProduct"
              @click="submitProduct"
            />
          </div>

          <div
            v-if="isLoadingProducts"
            class="rounded-xl border border-n-container px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCTS_LOADING') }}
          </div>
          <div
            v-else-if="!filteredProducts.length && products.length"
            class="rounded-xl border border-dashed border-n-container px-4 py-8 text-center text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_FILTER_EMPTY')
            }}
          </div>
          <div v-else class="grid gap-3">
            <div
              v-for="product in filteredProducts"
              :key="product.id || product.retailer_id"
              class="rounded-xl border border-n-container bg-n-background p-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ product.name || product.retailer_id }}
                  </p>
                  <p class="mt-1 mb-0 text-xs text-n-slate-11">
                    {{ product.retailer_id }} · {{ product.price }}
                    {{ product.currency }}
                  </p>
                  <p class="mt-1 mb-0 text-xs font-medium text-n-slate-10">
                    {{ product.availability || 'in stock' }}
                  </p>
                  <p
                    v-if="product.description"
                    class="mt-2 mb-0 text-xs text-n-slate-11"
                  >
                    {{ product.description }}
                  </p>
                </div>
                <div class="flex flex-col items-end gap-2">
                  <img
                    v-if="product.image_url"
                    :src="product.image_url"
                    alt=""
                    class="h-14 w-14 rounded-lg border border-n-container object-cover"
                  />
                  <div class="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="faded"
                    color="slate"
                    icon="i-ph-pencil-simple"
                    :label="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_EDIT'
                      )
                    "
                    @click="startEditingProduct(product)"
                  />
                  <Button
                    type="button"
                    variant="faded"
                    color="ruby"
                    icon="i-ph-trash"
                    :label="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCT_DELETE'
                      )
                    "
                    :is-loading="deletingProductId === product.id"
                    @click="deleteProduct(product)"
                  />
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="!products.length"
              class="rounded-xl border border-dashed border-n-container px-4 py-8 text-center text-sm text-n-slate-11"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_PRODUCTS_EMPTY')
              }}
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
